import { useEmbarcacaoDimensoes } from "@/modules/embarcacoes";
import type { EmbarcacaoDimensoes, EmbarcacaoDimensoesField } from "@/modules/embarcacoes";
import type { EmptyStateContent } from "@/screens/control-panel/components/empty-state";
import {
  DetailField,
  DetailSection,
  DetailSectionFields,
  DetailSectionState,
} from "../detail-section";
import { formatDetailDate, formatDetailNumber } from "../embarcacao-detail-format";
import type { EmbarcacaoDetailSectionProps } from "../embarcacao-detail-section";

export const DIMENSOES_PRINCIPAIS_ID = "dimensoes-principais";
export const DIMENSOES_PRINCIPAIS_TITLE = "Dimensões Principais";
export const DIMENSOES_PRINCIPAIS_LOADING_MESSAGE =
  "Carregando as Dimensões Principais da embarcação...";

/** Padrão global de estado vazio do Hub para a seção sem informações (AC4). */
const DIMENSOES_PRINCIPAIS_EMPTY_STATE: EmptyStateContent = {
  title: "Nenhuma informação de Dimensões Principais disponível",
  description:
    "Assim que houver dados dimensionais para esta embarcação, eles aparecerão nesta seção.",
};

const TEST_ID = "embarcacao-dimensoes-principais";

interface DimensoesFieldDefinition {
  readonly key: EmbarcacaoDimensoesField;
  /** Rótulo do protótipo, já com a unidade de medida quando ela é prevista. */
  readonly label: string;
  /** Tratamento do valor textual; hoje só as datas de conjunto (AC3). */
  readonly format?: (value: string) => string;
}

/**
 * Os 24 campos da seção, na ordem do AC2 — os 21 campos dimensionais e as três
 * datas de conjunto.
 *
 * A ordem vem dos agrupamentos da planilha de origem (Dimensões Principais,
 * Cargo Deck e Plano de docagem), mas os agrupamentos não aparecem na tela: por
 * decisão do PO a seção é uma lista contínua, como a Base Técnica (AC2). As
 * datas de cada conjunto entram como campos comuns, cada uma com o seu rótulo,
 * de modo que nenhuma seja lida como a data de outro conjunto (RN006/AC3).
 *
 * O grid distribui os campos em duas colunas, preenchendo a linha da esquerda
 * para a direita; nenhum campo precisa abrir linha nesta seção.
 */
export const DIMENSOES_PRINCIPAIS_FIELDS: DimensoesFieldDefinition[] = [
  { key: "comprimentoTotal", label: "Comprimento total (m)" },
  { key: "comprimentoEntrePerpendiculares", label: "Comprimento entre perpendiculares (m)" },
  { key: "caladoMax", label: "Calado máx (m)" },
  { key: "caladoDocagem", label: "Calado Docagem (m)" },
  { key: "bocaMoldada", label: "Boca moldada (m)" },
  { key: "arqueacaoBruta", label: "Arqueação bruta" },
  { key: "arqueacaoLiquida", label: "Arqueação líquida" },
  { key: "tonelagemPorteBruto", label: "Tonelagem porte bruto (T)" },
  {
    key: "atualizacaoDimensoes",
    label: "Data Atualização Dimensões Principais",
    format: formatDetailDate,
  },
  { key: "comprimentoConves", label: "Comprimento do Convés (m)" },
  { key: "larguraConves", label: "Largura do Convés (m)" },
  { key: "areaTotalConves", label: "Área total do Convés (m²)" },
  { key: "resistenciaConves", label: "Resistência do Convés (T/m²)" },
  { key: "capacidadeConves", label: "Capacidade do Convés (T)" },
  { key: "comprimentoConvesLivreUtil", label: "Comprimento do Convés Livre Útil (m)" },
  { key: "larguraConvesLivreUtil", label: "Largura do Convés Livre Útil (m)" },
  { key: "areaConvesLivreUtil", label: "Área do Convés Livre Útil (m²)" },
  { key: "atualizacaoCargoDeck", label: "Data Atualização Cargo Deck", format: formatDetailDate },
  { key: "deslocamentoCaladoDocagem", label: "Deslocamento calado docagem (T)" },
  { key: "caladoHidrostaticoDocagem", label: "Calado hidrostático de docagem (m)" },
  { key: "tamanhoApendices", label: "Tamanho dos apêndices (m)" },
  { key: "equipamentoRetratil", label: "Equipamento retrátil" },
  { key: "caladoRealDocagem", label: "Calado real de docagem (m)" },
  { key: "atualizacaoDocagem", label: "Data Atualização Docagem", format: formatDetailDate },
];

/** Valor do campo pronto para a tela; `null` segue como campo sem preenchimento. */
function fieldValue(
  dimensoes: EmbarcacaoDimensoes,
  { key, format }: DimensoesFieldDefinition,
): string | null {
  const value = dimensoes[key];
  if (value === null) return null;

  // Campo dimensional: só o idioma da tela é aplicado, sem cálculo (RN005).
  if (typeof value === "number") return formatDetailNumber(value);

  return format ? format(value) : value;
}

/**
 * Seção Dimensões Principais do detalhamento da embarcação (US-1794).
 *
 * Estruturada exatamente como as demais seções: um título, uma data de última
 * atualização no cabeçalho e uma lista contínua de campos em duas colunas
 * (AC2). Exclusivamente de leitura (RN003): nenhum botão ou campo de cadastro,
 * edição ou exclusão.
 */
export function DimensoesPrincipaisSection({
  embarcacaoId,
  onReauthenticate,
}: EmbarcacaoDetailSectionProps) {
  const { status, dimensoes, errorMessage, reload } = useEmbarcacaoDimensoes(embarcacaoId);

  return (
    <DetailSection
      id={DIMENSOES_PRINCIPAIS_ID}
      title={DIMENSOES_PRINCIPAIS_TITLE}
      // Só há data de atualização a exibir quando a seção tem dados.
      ultimaAtualizacao={dimensoes ? dimensoes.ultimaAtualizacao : undefined}
      testId={TEST_ID}
    >
      {dimensoes ? (
        <DetailSectionFields testId={`${TEST_ID}-fields`}>
          {DIMENSOES_PRINCIPAIS_FIELDS.map((field) => (
            <DetailField
              key={field.key}
              label={field.label}
              value={fieldValue(dimensoes, field)}
              testId={`${TEST_ID}-${field.key}`}
            />
          ))}
        </DetailSectionFields>
      ) : (
        <DetailSectionState
          // `success` sempre traz os campos: aqui só chegam carregamento e exceções.
          status={status === "success" ? "error" : status}
          loadingMessage={DIMENSOES_PRINCIPAIS_LOADING_MESSAGE}
          errorMessage={errorMessage}
          emptyContent={DIMENSOES_PRINCIPAIS_EMPTY_STATE}
          onReauthenticate={onReauthenticate}
          onRetry={reload}
          testId={TEST_ID}
        />
      )}
    </DetailSection>
  );
}
